import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { Paper, Box, Typography, Tabs, Tab, useTheme } from '@mui/material';
import PropTypes from 'prop-types';

/** HTTP Client Liabrary. */
import axios from 'axios';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from '../../state/slices/snackBarSlice';

/** Internal Components. */
import CustomSkeleton from '../../components/Skeleton';
import AccountTable from './AccountTable';
import DeletedTable from './DeletedTable';
import { callAPI } from '../../api/useApiRequest';
import CustomSnackbar from '../../components/CustomSnackbar';

/** Create tabPanel component. */
function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 2 }}>
                    <Typography component="div">{children}</Typography>
                </Box>
            )}
        </div>
    );
}

/** To run typechecking on the props for a component. */
TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

export default function Account() {
    /** Retrieves user information and access token silently from Auth0 for authentication and API requests. */
    const { user, getAccessTokenSilently } = useAuth0();

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Use the useTheme hook. */
    const theme = useTheme();

    /** Tab index value. */
    const [tabValue, setTabValue] = useState(0);
    /** State to display loading component until API response is received. */
    const [loaded, setLoaded] = useState(false);

    /** State for registered account info. */
    const [accounts, setAccounts] = useState([]);
    /** State for deleted account info. */
    const [deleted, setDeleted] = useState([]);
    /** State for Ref1, Ref2 List. */
    const [reference, setReference] = useState([]);
    /** Data for Deleted Account Datagrid row. */
    const [delRow, setDelRow] = useState([]);

    /** Named website title. */
    useEffect(() => {
        document.title = 'Account Setting | Management';
    }, []);

    /** Handler to change tabValue . */
    const handleChange = (event, newValue) => {
        setTabValue(newValue);
    };

    /** Update the deleted row state when the deleted accounts change. */
    useEffect(() => {
        setDelRow(deleted);
    }, [deleted]);

    useEffect(() => {
        callAPI({
            method: 'get',
            module: 'accounts',
            component: null,
            type: 'accounts',
            getAccessTokenSilently,
            email: user.email,
            onSuccess: (resp) => {
                setAccounts(resp.data.body.accounts);
                setReference(resp.data.body.references);
                setDeleted(resp.data.body.deleted);
            },
            onError: (err) => {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Someting went wrong. Please try again.',
                        severity: 'error',
                    }),
                );
            },
            onFinal: () => {
                setLoaded(true);
            },
        });
    }, [getAccessTokenSilently, user.email]);

    return (
        <Box maxWidth="97%" sx={{ mt: 2, mx: 'auto', height: '90vh' }}>
            <Paper
                sx={{
                    borderRadius: 5,
                    height: '100%',
                }}
            >
                {loaded ? (
                    <>
                        <Tabs value={tabValue} onChange={handleChange} variant="fullWidth">
                            <Tab label="User Account List" />
                            <Tab label="Deleted List" />
                        </Tabs>
                        <TabPanel value={tabValue} index={0} dir={theme.direction}>
                            <AccountTable
                                email={user.email}
                                getAccessTokenSilently={getAccessTokenSilently}
                                accounts={accounts}
                                setAccounts={setAccounts}
                                reference={reference}
                                setDeleted={setDeleted}
                            />
                        </TabPanel>
                        <TabPanel value={tabValue} index={1} dir={theme.direction}>
                            <DeletedTable email={user.email} deleted={deleted} delRow={delRow} setDelRow={setDelRow} />
                        </TabPanel>
                    </>
                ) : (
                    <CustomSkeleton />
                )}
            </Paper>
            <CustomSnackbar />
        </Box>
    );
}
